import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  confirmRefund,
  executeAction,
  executePayment,
  getHistory,
  getRequest,
  markPosted,
  releaseCash,
  retireAdvance,
  setAllocation,
  setBankDetails,
  setReceiptStatus,
} from "../api/requests";
import { ACTION_LABELS, ApiError, type AuditEntry, type AvailableAction } from "../api/types";
import { Attachments } from "../components/Attachments";
import { Money } from "../components/Money";

type Detail = Record<string, unknown>;

type ExpenseLine = {
  lineId?: string;
  lineNumber?: number;
  description?: string;
  expenseDate?: string;
  projectCode?: string | null;
  costCentreCode?: string | null;
  amountNgn?: number;
};

/**
 * Actions requiring a written reason.
 *
 * The workflow definitions mark RETURN and REJECT as requiring a comment, and
 * the API refuses them without one. Asking here rather than discovering the
 * refusal keeps the round trip out of the way -- and a rejection with no
 * stated reason is exactly what the paper process avoided by having a person
 * write on the form.
 */
const REQUIRES_COMMENT = new Set(["RETURN", "REJECT"]);

/**
 * Actions that carry captured data and therefore have their own endpoint and
 * their own panel below. They are excluded from the generic button row: a bare
 * button for MARK_POSTED would send the action with no BC document number,
 * the guard would refuse it for lacking one, and the message would be true but
 * useless -- there was nowhere to type it.
 */
const CAPTURE_ACTIONS = new Set([
  "MARK_POSTED",
  "EXECUTE_PAYMENT",
  "CONFIRM_REFUND",

  // RELEASE_CASH captures CashReleasedAt, and was missing from this set until
  // 7 September 2026 -- so it rendered as a generic button that would have
  // sent the action with no date, been refused for the missing field, and
  // offered nowhere to supply it. Exactly the deadlock the comment above
  // describes, in the one branch nobody had walked.
  "RELEASE_CASH",
]);

/**
 * Actions the engine performs, which no person may press.
 *
 * RETIRE is not something a requester does. An advance is retired *by* an
 * expense claim: the requester raises the claim through the retirement flow,
 * and AdvanceRetirementHandler fires RETIRE against the advance as a cascade
 * once that claim carries a real figure. The transition exists to record the
 * consequence, not to offer a choice.
 *
 * It was rendered as a plain button until 7 September 2026, because
 * GetAvailableActionsAsync correctly reports it as available -- its actor is
 * the Requester and its guard only asks that a balance remains. Pressing it
 * fired the transition with no claim, no receipts and no amount behind it. On
 * ADV-2026-000008 that took a ₦360,000 advance from OUTSTANDING to
 * PARTIALLY_RETIRED with nothing retired, twice, and wrote two audit entries
 * saying a retirement had happened.
 *
 * Same shape as MARK_POSTED and RELEASE_CASH before it: an action that carries
 * data offered as a bare button. The difference is that those two were refused
 * by the API. This one succeeded.
 */
const SYSTEM_ACTIONS = new Set(["RETIRE"]);

const money = (value: number) =>
  new Intl.NumberFormat("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    value,
  );

export function RequestDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");

  // Capture state. Kept separate from `detail` because these are things the
  // user is typing, not things the server has said.
  const [bcDocumentNumber, setBcDocumentNumber] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [refundAmount, setRefundAmount] = useState("");
  const [treasuryNumber, setTreasuryNumber] = useState("");

  // When the cash actually left. Starts the retirement clock, so it is the
  // date the advance becomes overdue from -- not a formality.
  const [cashReleasedAt, setCashReleasedAt] = useState(() =>
    new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16),
  );

  // Cost Control setting the coding. Separate state from the action
  // panel because it is not an action: nothing moves, a fact about the
  // request is corrected while it sits in the same queue.
  const [codeKind, setCodeKind] = useState<"CostCentre" | "Project">("CostCentre");
  const [code, setCode] = useState("");
  const [codeReason, setCodeReason] = useState("");
  const [codingBusy, setCodingBusy] = useState(false);

  // The requester answering the receipts question on a claim already in their
  // hands. Its own flag rather than the shared `busy`, so the radio row does
  // not disable the action buttons underneath it.
  const [receiptBusy, setReceiptBusy] = useState(false);

  // Where the money actually goes, recorded by the desk that pays rather than
  // the person who raised the claim. Kept out of the requester's form on
  // purpose: the payment guard refuses anyone who both set the account and
  // approves paying into it.
  const [bankName, setBankName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, h] = await Promise.all([getRequest(id), getHistory(id)]);
      setDetail(d);
      setHistory(h);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Shared wrapper: every capture panel and the generic buttons run through this. */
  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    try {
      await work();
      setComment("");
      await load();
    } catch (e) {
      // A guard rejection arrives as ProblemDetails with a human-readable
      // detail. Show it verbatim: it names the condition that failed, which
      // is more use than anything this screen could invent.
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function act(action: string) {
    if (REQUIRES_COMMENT.has(action) && comment.trim().length === 0) {
      setError(`${ACTION_LABELS[action] ?? action} needs a reason.`);
      return;
    }

    // No payload. VERIFY at COST_CONTROL_VERIFY used to carry a Treasury
    // number; workflow version 6 moved that capture to MARK_POSTED, where
    // Treasury acts. Cost Control now guards on the coding instead, which
    // is what its own note always said the step was for.
    await run(() => executeAction(id, action, comment.trim() || undefined));
  }

  if (!detail) {
    return error ? (
      <p className="rounded bg-red-50 p-4 text-red-800">{error}</p>
    ) : (
      <p className="p-4 text-gray-500">Loading…</p>
    );
  }

  const availableActions = (detail.availableActions as AvailableAction[] | undefined) ?? [];
  const requestNumber = String(detail.requestNumber ?? "");
  const currentState = String(detail.currentState ?? "");
  const total = Number(detail.totalAmountNgn ?? 0);
  const netPayable = Number(detail.netPayableNgn ?? total);
  const lines = (detail.lines as ExpenseLine[] | undefined) ?? [];

  // Null for a cash advance, which pays its requester, and on a claim whose
  // beneficiary the API has not resolved yet.
  const payee = detail.beneficiary as
    | {
        name: string;
        type: string;
        staffNumber: string | null;
        email: string | null;
        hasBankDetails: boolean;
      }
    | null
    | undefined;

  // Who raised it, and out of which department.
  //
  // Reported by Cost Control on 24 August 2026: "Request did not show which
  // department the request is coming from." The ids were always in the
  // response — requesterId and departmentId, both GUIDs — so the fact was on
  // the wire and off the page. Cost Control's entire question is whether an
  // advance is costed to the right centre, and they were being asked it
  // without being told whose department it came from.
  const raisedBy = detail.requester as
    | { name: string; staffNumber: string | null; department: string | null }
    | null
    | undefined;

  // How this request is costed.
  //
  // A cash advance is coded once, on the form; an expense claim is coded per
  // line. The table below renders the per-line column, so for an advance it
  // showed "—" on every row and the coding appeared nowhere at all — the
  // Head of Department approved without seeing it and Cost Control, whose
  // whole job is to check it, could not read the thing being checked.
  //
  // The API has sent allocationType, projectCode and costCentreCode in the
  // cash advance detail since it was written. Third time on this project that
  // a fact was on the wire and off the page: the payee in August, the
  // requester's department last week, and now this. The response was correct
  // every time, which is exactly why nothing caught it.
  const allocationType = detail.allocationType as string | undefined;
  const requestProjectCode = (detail.projectCode as string | null | undefined) ?? null;
  const requestCostCentreCode = (detail.costCentreCode as string | null | undefined) ?? null;
  const requestCoding = requestProjectCode ?? requestCostCentreCode;

  // DEL-AC-FRM-002 and DEL-AC-FRM-003 do not have the same table.
  //
  // The expense form itemises date, description, project or cost centre, and
  // amount. The cash advance form has description and amount, and nothing
  // else -- it is a request for money not yet spent, so there is no date a
  // line was incurred on and no per-line coding, because an advance is coded
  // once on the form.
  //
  // One table was drawn for both, so a cash advance showed a Date column and a
  // Project / Cost centre column that could only ever contain a dash. Two
  // columns of nothing, on the screen an approver is meant to read, next to a
  // total they are being asked to authorise. Reported as "no date shown",
  // which is exactly how it looks: like data that failed to load rather than a
  // column that should not have been drawn.
  const isAdvance = String(detail.moduleKey ?? "") === "CASH_ADVANCE";
  const genericActions = availableActions.filter(
    (a) => !CAPTURE_ACTIONS.has(a.action) && !SYSTEM_ACTIONS.has(a.action),
  );

  /**
   * Authorised, whether or not the guard passes yet.
   *
   * This is what the capture panels key on, and the distinction is the whole
   * point: MARK_POSTED's guard requires a BC document number, so gating the
   * field on the guard passing means the field never appears and the number
   * can never be entered. Same for the Treasury number and the refund amount.
   */
  const can = (action: string) => availableActions.some((a) => a.action === action);

  const blockedReason = (action: string) =>
    availableActions.find((a) => a.action === action && !a.isEnabled)?.blockedReason ?? null;

  /**
   * Whether something typed on this screen resolves a block the server
   * reported.
   *
   * `isEnabled` answers "could you take this action against what is stored".
   * For an action that carries its own captured data, the answer is no right
   * up until the moment it is taken — the Treasury number travels *with*
   * VERIFY, so it cannot be stored before the click that stores it. Gating the
   * button on the server's answer alone means typing the number changes
   * nothing and the button never comes alive.
   *
   * This is the same deadlock as the one that hid the field, one layer up:
   * fixing where the input renders was not enough while the button still
   * asked the impossible question.
   *
   * Optimistic, and safe to be. The guard runs again inside the transaction;
   * if this screen is wrong the transition is refused and the guardMessage
   * comes back verbatim. The cost of being wrong is a round trip. The cost of
   * being conservative is a state nobody can leave.
   */
  function locallyUnblocked(_action: string) {
    // Nothing typed on this screen unblocks a generic action any more.
    // VERIFY at COST_CONTROL_VERIFY was the only case, and its guard is now
    // the request's coding -- which is set through the Coding panel and
    // reloaded from the server, so the server's own isEnabled is accurate.
    return false;
  }

  return (
    <div className="space-y-6">
      <header className="rounded border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-xl font-semibold text-gray-900">{requestNumber}</h1>
          <Money amountNgn={total} />
        </div>
        <p className="mt-1 text-sm text-gray-600">
          {currentState.replaceAll("_", " ")}
          {detail.formCode ? ` · ${String(detail.formCode)} ${String(detail.formRevision ?? "")}` : ""}
        </p>

        {requestCoding ? (
          <p className="mt-2 text-sm text-gray-700">
            <span className="text-gray-500">
              {allocationType === "Project" ? "Project" : "Cost centre"}
            </span>{" "}
            <span className="font-medium">{requestCoding}</span>
          </p>
        ) : allocationType ? (
          <p className="mt-2 text-sm text-amber-700">
            No project or cost centre recorded.
          </p>
        ) : null}

        {raisedBy ? (
          <p className="mt-2 text-sm text-gray-700">
            <span className="text-gray-500">Raised by</span>{" "}
            <span className="font-medium">{raisedBy.name}</span>
            {raisedBy.staffNumber ? (
              <span className="text-gray-500"> ({raisedBy.staffNumber})</span>
            ) : null}
            {raisedBy.department ? (
              <>
                {" · "}
                <span className="text-gray-500">Department</span>{" "}
                <span className="font-medium">{raisedBy.department}</span>
              </>
            ) : null}
          </p>
        ) : null}

        {/* Who gets the money.

            This screen showed no payee at all until 9 August 2026 — the API
            returned a beneficiary id and nothing rendered it. Every approver
            in the chain, up to and including the Director of Finance whose
            entire function is authorising money to leave, was approving a
            payment without the recipient appearing anywhere on the page.

            It surfaced when a claim was raised against the wrong one of two
            employees sharing a display name and was paid. Nobody was careless;
            there was nothing on screen to be careful about. Staff number and
            email are here for that reason — a name is not an identifier. */}
        {payee && (
          <p className="mt-1 text-sm text-gray-800">
            Payable to <span className="font-medium">{payee.name}</span>
            {payee.staffNumber || payee.email ? (
              <span className="text-gray-600">
                {payee.staffNumber ? ` · ${payee.staffNumber}` : ""}
                {payee.email ? ` · ${payee.email}` : ""}
              </span>
            ) : (
              <span className="text-gray-600"> · {payee.type}</span>
            )}
          </p>
        )}

        {/* Who collects the cash on an advance.

            Deliberately worded as collection rather than payment, and shown
            without a staff number or an email, because unlike the payee above
            it identifies nobody in this system -- it is a name the requester
            typed so Treasury knows who to hand the money to. Saying "payable
            to" here would imply an account that does not exist. */}
        {isAdvance && detail.beneficiaryName ? (
          <p className="mt-1 text-sm text-gray-800">
            To be collected by{" "}
            <span className="font-medium">{String(detail.beneficiaryName)}</span>
            <span className="text-gray-600">
              {" "}
              · advance remains the requester&apos;s to retire
            </span>
          </p>
        ) : null}

        {/* Net payable differs from the total only when an advance was taken
            against this claim, and the difference is the whole point of the
            REFUND_DUE branch -- so it is shown only when it says something the
            total does not. */}
        {netPayable !== total && (
          <p className="mt-1 text-sm text-gray-700">
            Net payable <Money amountNgn={netPayable} />
            {netPayable < 0 ? " · refund due from employee" : ""}
          </p>
        )}

        {detail.paymentReference ? (
          <p className="mt-1 text-sm text-gray-700">
            Paid · reference {String(detail.paymentReference)}
          </p>
        ) : null}
      </header>

      {error && (
        <p role="alert" className="rounded bg-red-50 p-4 text-red-800">
          {error}
        </p>
      )}

      {/* What is actually being approved. An approver looking at a single
          total is approving a number, not a claim -- the paper form put the
          itemised table directly above the signature block for that reason. */}
      {lines.length > 0 && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {isAdvance ? "Details of the advance" : "Details of expense"}
          </h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                  <th className="py-2 pr-2 font-medium">#</th>
                  {isAdvance ? null : <th className="py-2 pr-2 font-medium">Date</th>}
                  <th className="py-2 pr-2 font-medium">Description</th>
                  {isAdvance ? null : (
                    <th className="py-2 pr-2 font-medium">Project / Cost centre</th>
                  )}
                  <th className="py-2 pl-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.lineId ?? index} className="border-b border-gray-100">
                    <td className="py-2 pr-2 text-gray-500">{line.lineNumber ?? index + 1}.0</td>
                    {isAdvance ? null : (
                      <td className="py-2 pr-2 tabular-nums">
                        {line.expenseDate ? String(line.expenseDate).slice(0, 10) : "—"}
                      </td>
                    )}
                    <td className="py-2 pr-2">{line.description}</td>
                    {isAdvance ? null : (
                      <td className="py-2 pr-2 text-gray-600">
                        {line.projectCode ?? line.costCentreCode ?? "—"}
                      </td>
                    )}
                    <td className="py-2 pl-2 text-right tabular-nums">
                      {money(Number(line.amountNgn ?? 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Evidence sits directly above the actions, so an approver sees what
          was purchased in the same glance as the button that approves it.
          Reloading on change matters: Cost Control's guard counts attachments,
          so uploading one can turn a disabled Verify live. */}
      <Attachments
        requestId={id}
        canUpload={!detail.closedAt}
        onChanged={() => void load()}
      />

      {/* --- Attached receipts (the requester's own claim, still with them) ---

          Gated on can("SUBMIT") rather than on the viewer's identity: that is
          already exactly "you are authorised to submit this", which means the
          requester, in DRAFT or RETURNED. And `can` deliberately ignores
          whether the guard passes, which is the whole reason this panel works
          — SUBMIT is blocked precisely when this field says No.

          Why it exists at all: retiring an advance creates the claim as a
          direct insert that never sets ReceiptStatus, so it arrives as No, and
          EXPENSE's SUBMIT guard refuses No. There was no screen anywhere that
          could change it. A requester who pressed "Retire this advance" landed
          on a claim they could neither submit nor edit, which is why almost
          nothing was ever retired. See docs/15 section 5h. */}
      {!isAdvance && can("SUBMIT") && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-800">Attached receipts</p>
          <p className="mt-1 text-xs text-gray-600">
            Your answer about this claim. The files above are the receipts themselves —
            both are needed to submit.
          </p>

          <div className="mt-2 flex flex-wrap gap-4">
            {(["Yes", "No", "Incomplete"] as const).map((option) => (
              <label key={option} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="detailReceiptStatus"
                  checked={String(detail.receiptStatus ?? "") === option}
                  disabled={receiptBusy}
                  onChange={() => {
                    setReceiptBusy(true);
                    setReceiptStatus(id, option)
                      .then(() => load())
                      .catch((e: unknown) =>
                        setError(e instanceof ApiError ? e.message : (e as Error).message),
                      )
                      .finally(() => setReceiptBusy(false));
                  }}
                />
                {option}
              </label>
            ))}
          </div>

          {String(detail.receiptStatus ?? "") === "No" && (
            <p className="mt-2 text-xs text-gray-700">
              A claim recording no receipts cannot be submitted. Attach them above and
              change this to Yes, or to Incomplete if you only have some.
            </p>
          )}
        </section>
      )}

      {/* --- Bank details (expense claims whose payee has none) ---

          A requester can name a payee who is not on the list, and such a payee
          has no account behind them. Above ₦30,000 the payment method is
          bank transfer whatever anyone prefers, and EXECUTE_PAYMENT guards on
          BeneficiaryHasBankDetails — so without this the claim reaches Treasury
          and stops, with the posting already made in Business Central.

          Shown to whoever holds the claim rather than gated on a role: the
          endpoint has always been scoped to the claim, and the control that
          matters is not who types the account but that the same person cannot
          then approve paying into it. That is enforced at the payment guard
          (ActorId != BeneficiaryBankDetailsSetByUserId), which no screen can
          talk its way past.

          The endpoint shipped in the first release and had no caller until
          10 September 2026. */}
      {!isAdvance && payee && !payee.hasBankDetails && !detail.closedAt && (
        <section className="rounded border border-amber-300 bg-amber-50 p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-900">
            Bank details needed
          </h2>
          <p className="mt-1 text-sm text-amber-900">
            <span className="font-medium">{payee.name}</span> has no account on file, so this
            claim cannot be paid by transfer. Record it here — whoever does cannot also
            approve the payment into it.
          </p>

          <div className="mt-3 flex flex-wrap gap-3">
            <div>
              <label className="block text-sm text-gray-700" htmlFor="bank-name">
                Bank name
              </label>
              <input
                id="bank-name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g. Zenith Bank"
                className="mt-1 min-h-11 w-56 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700" htmlFor="bank-account">
                Account number
              </label>
              <input
                id="bank-account"
                value={bankAccountNumber}
                onChange={(e) => setBankAccountNumber(e.target.value)}
                inputMode="numeric"
                placeholder="10 digits"
                className="mt-1 min-h-11 w-56 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>

          <button
            type="button"
            disabled={
              busy || bankName.trim().length === 0 || bankAccountNumber.trim().length === 0
            }
            onClick={() =>
              void run(async () => {
                await setBankDetails(id, bankName.trim(), bankAccountNumber.trim());
                setBankName("");
                setBankAccountNumber("");
              })
            }
            className="mt-3 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Record bank details
          </button>
        </section>
      )}

      {/* --- Business Central posting (AWAITING_POSTING) --- */}
      {can("MARK_POSTED") && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Posting in Business Central
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Post this in Business Central first, then record the document number here. The journal
            itself lives in BC — this records that it was posted and where to find it.
          </p>

          <label className="mt-3 block text-sm text-gray-700" htmlFor="bc-document">
            BC document number
          </label>
          <input
            id="bc-document"
            value={bcDocumentNumber}
            onChange={(e) => setBcDocumentNumber(e.target.value)}
            placeholder="Business Central posting or document number"
            className="mt-1 min-h-11 w-72 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
          />

          <label className="mt-3 block text-sm text-gray-700" htmlFor="treasury">
            Treasury number
          </label>
          <input
            id="treasury"
            value={treasuryNumber}
            onChange={(e) => setTreasuryNumber(e.target.value)}
            placeholder="Treasury reference"
            className="mt-1 min-h-11 w-72 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
          />

          <p className="mt-2 text-sm text-gray-500">
            {isAdvance
              ? "Once posted, this moves to cash release."
              : netPayable > 0
                ? "Once posted, this moves to payment."
                : "Nothing is payable on this claim, so it closes once posted."}
          </p>

          <button
            type="button"
            disabled={
              busy || bcDocumentNumber.trim().length === 0 || treasuryNumber.trim().length === 0
            }
            onClick={() =>
              void run(() =>
                markPosted(
                  id,
                  String(detail.moduleKey ?? ""),
                  bcDocumentNumber.trim(),
                  treasuryNumber.trim(),
                  comment.trim() || undefined,
                ),
              )
            }
            className="mt-3 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Mark posted in BC
          </button>
        </section>
      )}

      {/* --- Cash release (CASH_RELEASE) --- Cash advances only.

          Added 7 September 2026, after Treasury reached AWAITING_POSTING on
          ADV-2026-000008 -- the first advance ever to get that far with a
          person behind it -- and found the next three steps had no screen at
          all. The API endpoints all existed and were all tested. Nothing in
          the browser called them.

          CashReleasedAt is what starts the retirement clock. Every overdue
          advance, the SUBMIT guard that blocks a new one, and the Director of
          Finance's whole objection about advances never being retired all
          hang off this single field. */}
      {can("RELEASE_CASH") && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Cash release
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Record when the cash actually left. This starts the retirement clock — the advance
            becomes overdue counted from this moment, not from today.
          </p>

          <label className="mt-3 block text-sm text-gray-700" htmlFor="cash-released-at">
            Released at
          </label>
          <input
            id="cash-released-at"
            type="datetime-local"
            value={cashReleasedAt}
            onChange={(e) => setCashReleasedAt(e.target.value)}
            className="mt-1 min-h-11 w-72 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
          />

          <p className="mt-2 text-sm text-gray-500">
            The requester then acknowledges receipt, and the advance becomes outstanding until
            it is retired.
          </p>

          <button
            type="button"
            disabled={busy || cashReleasedAt.length === 0}
            onClick={() =>
              void run(() =>
                releaseCash(
                  id,
                  new Date(cashReleasedAt).toISOString(),
                  comment.trim() || undefined,
                ),
              )
            }
            className="mt-3 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Record cash release
          </button>
        </section>
      )}

      {/* --- Retirement (OUTSTANDING / PARTIALLY_RETIRED) --- Cash advances only.

          The paper process: you took cash, you bought something, you kept the
          receipts, and you accounted for it on an expense form with the advance
          written against it. This is that, and the button below raises the
          claim for you rather than asking you to start a blank one -- because a
          claim raised from New Expense would be an ordinary reimbursement and
          would never net off what you already took.

          Replaces the bare RETIRE button that used to appear here. See
          SYSTEM_ACTIONS above for what that did. */}
      {isAdvance &&
        ["OUTSTANDING", "PARTIALLY_RETIRED"].includes(String(detail.currentState ?? "")) && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Retire this advance
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Account for what the cash was spent on. This opens an expense claim already
            linked to this advance and carrying the outstanding balance — attach your
            purchase receipts to it and submit it as normal.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            The advance closes when that claim is approved. If you spent less than you took,
            the balance stays against your name until it is returned.
          </p>

          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const draft = await retireAdvance(id);
                navigate(`/requests/${draft.expenseRequestId}`);
              })
            }
            className="mt-3 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Start retirement claim
          </button>
        </section>
      )}

      {/* Once recorded, the reference is the thread back to the ledger. Shown
          to everyone who can see the request, not just Accounts: "where is this
          in BC" is the question asked when a payment is queried. */}
      {detail.bcDocumentNumber && !can("MARK_POSTED") ? (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Business Central
          </h2>
          <p className="mt-2 text-sm text-gray-700">
            Posted as document{" "}
            <span className="font-medium tabular-nums">{String(detail.bcDocumentNumber)}</span>
          </p>
        </section>
      ) : null}

      {/* --- Payment (AWAITING_PAYMENT → AWAITING_ACK) --- */}
      {can("EXECUTE_PAYMENT") && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Release payment
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Payment method is {String(detail.paymentMethod ?? "")}, derived from the amount against
            the policy threshold — it is not chosen here. The reference is what the beneficiary
            will be asked to confirm against.
          </p>

          {/* The guard's own words when payment is blocked — most often that
              the beneficiary has no account number on file, which is fixable
              and which nothing else on this screen would explain. */}
          {blockedReason("EXECUTE_PAYMENT") && (
            <p className="mt-2 text-sm text-amber-800">{blockedReason("EXECUTE_PAYMENT")}</p>
          )}

          <div className="mt-3 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-gray-700" htmlFor="payment-reference">
                Payment reference
              </label>
              <input
                id="payment-reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Bank transfer or cash voucher reference"
                className="mt-1 min-h-11 w-72 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700" htmlFor="payment-date">
                Payment date
              </label>
              <input
                id="payment-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1 min-h-11 rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>

          <button
            type="button"
            disabled={
              busy ||
              paymentReference.trim().length === 0 ||
              blockedReason("EXECUTE_PAYMENT") !== null
            }
            onClick={() =>
              void run(() =>
                executePayment(
                  id,
                  paymentReference.trim(),
                  paymentDate ? `${paymentDate}T00:00:00Z` : undefined,
                  comment.trim() || undefined,
                ),
              )
            }
            className="mt-3 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Record payment
          </button>
        </section>
      )}

      {/* --- Refund received (REFUND_DUE → POSTING) --- */}
      {can("CONFIRM_REFUND") && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Refund received
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            The employee spent less than the advance taken. This must equal{" "}
            <Money amountNgn={Math.abs(netPayable)} /> exactly — the guard compares to the naira.
          </p>
          <input
            aria-label="Refund amount received"
            type="number"
            step="0.01"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            className="mt-3 min-h-11 w-48 rounded border border-gray-300 p-2 text-right text-sm tabular-nums"
          />
          <button
            type="button"
            disabled={busy || Number(refundAmount) <= 0}
            onClick={() =>
              void run(() => confirmRefund(id, Number(refundAmount), comment.trim() || undefined))
            }
            className="ml-2 min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Confirm refund
          </button>
        </section>
      )}

      {availableActions.length > 0 && (
        <section className="rounded border border-gray-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Available actions
          </h2>

          {/* Cost Control holds the organisation's cost centres; the
              requester generally does not, so a code that arrives wrong or
              blank is ordinary rather than careless. Returning every one of
              them would make the desk that knows the answer ask the person
              who does not.

              Return stays where it is and is unchanged. It is the right
              response when the request is wrong; this is for when only the
              coding is. */}
          {currentState === "COST_CONTROL_VERIFY" && can("VERIFY") && (
            <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-800">Coding</p>
              <p className="mt-1 text-xs text-gray-600">
                Set or correct this before verifying. The previous coding is kept in the
                history.
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  aria-label="Coding type"
                  value={codeKind}
                  onChange={(e) => setCodeKind(e.target.value as "CostCentre" | "Project")}
                  className="min-h-11 rounded border border-gray-300 p-2 text-sm"
                >
                  <option value="CostCentre">Cost centre</option>
                  <option value="Project">Project</option>
                </select>

                <input
                  aria-label="Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={codeKind === "Project" ? "Project code" : "Cost centre code"}
                  className="min-h-11 w-48 rounded border border-gray-300 p-2 text-sm"
                />

                <input
                  aria-label="Reason for the coding change"
                  value={codeReason}
                  onChange={(e) => setCodeReason(e.target.value)}
                  placeholder="Why (recorded against the request)"
                  className="min-h-11 flex-1 rounded border border-gray-300 p-2 text-sm"
                />

                <button
                  type="button"
                  disabled={codingBusy || code.trim() === "" || codeReason.trim() === ""}
                  onClick={() => {
                    setCodingBusy(true);
                    setError(null);
                    setAllocation(id!, {
                      ...(codeKind === "Project"
                        ? { projectCode: code.trim() }
                        : { costCentreCode: code.trim() }),
                      reason: codeReason.trim(),
                    })
                      .then(() => {
                        setCode("");
                        setCodeReason("");
                        return load();
                      })
                      .catch((e: Error) =>
                        setError(e instanceof ApiError ? e.message : e.message),
                      )
                      .finally(() => setCodingBusy(false));
                  }}
                  className="min-h-11 rounded bg-desicon-navy px-4 py-2 text-sm font-medium text-white disabled:bg-gray-300"
                >
                  {codingBusy ? "Saving…" : "Set coding"}
                </button>
              </div>
            </div>
          )}

          <label className="mt-3 block text-sm text-gray-700" htmlFor="comment">
            Comment
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
            placeholder="Required when returning or rejecting"
          />

          {/* Buttons read Verify, Approve and Endorse -- the words printed on
              DEL-AC-FRM-002. A clerk should not have to learn a new vocabulary
              to do the job they already do. Large tap targets because the
              approval path is used from a phone. */}
          <div className="mt-3 flex flex-wrap gap-2">
            {genericActions.map(({ action, isEnabled }) => (
              <button
                key={action}
                type="button"
                disabled={busy || !(isEnabled || locallyUnblocked(action))}
                onClick={() => void act(action)}
                className={
                  action === "REJECT"
                    ? "min-h-11 rounded bg-red-700 px-4 py-2 font-medium text-white hover:bg-red-800 disabled:opacity-50"
                    : action === "RETURN"
                      ? "min-h-11 rounded border border-gray-400 px-4 py-2 font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                      : "min-h-11 rounded bg-blue-700 px-4 py-2 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
                }
              >
                {ACTION_LABELS[action] ?? action}
              </button>
            ))}
          </div>

          {/* Why a disabled button is disabled, in the definition's own words.
              Without this the screen shows a greyed-out Verify and no
              explanation, which is barely better than showing nothing --
              the guardMessage names the field to fill in. */}
          {/* Reasons disappear as they are resolved — a message still sitting
              under a button that has just come alive reads as a contradiction. */}
          {genericActions.some((a) => !a.isEnabled && !locallyUnblocked(a.action)) && (
            <ul className="mt-3 space-y-1">
              {genericActions
                .filter((a) => !a.isEnabled && !locallyUnblocked(a.action) && a.blockedReason)
                .map((a) => (
                  <li key={a.action} className="text-sm text-amber-800">
                    <span className="font-medium">{ACTION_LABELS[a.action] ?? a.action}:</span>{" "}
                    {a.blockedReason}
                  </li>
                ))}
            </ul>
          )}
        </section>
      )}

      <section className="rounded border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">History</h2>
        <ol className="mt-3 space-y-3">
          {history.map((entry) => (
            <li key={entry.auditEventId} className="border-l-2 border-gray-200 pl-3">
              <p className="text-sm font-medium text-gray-900">
                {ACTION_LABELS[entry.eventType] ?? entry.eventType}
                {entry.fromState && entry.toState ? (
                  <span className="font-normal text-gray-500">
                    {" "}
                    · {entry.fromState} → {entry.toState}
                  </span>
                ) : null}
              </p>
              {entry.reason && <p className="text-sm text-gray-700">{entry.reason}</p>}
              <p className="text-xs text-gray-500">
                {new Date(entry.occurredAtUtc).toLocaleString("en-NG")}
                {/* On behalf of is where delegation shows, and where
                    EscalationSweep records who failed to act. Worth surfacing:
                    "who was this waiting on" is the question the screen exists
                    to answer. */}
                {entry.onBehalfOfUserId ? " · on behalf of another user" : ""}
              </p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
